<template>
  <app-shell :refreshing="isLoading" @refresh="load">
    <template #overlays>
      <viewTicketModal
        v-if="showViewTicketModal"
        :selectedTicketToView="selectedTicket"
        @close-view-ticket-modal="showViewTicketModal = false"
        @refresh-tickets="load"
      />
    </template>

    <header class="pg-header">
      <div class="pg-header__titles">
        <h4 class="page-title">Dashboard</h4>
        <p class="pg-subtitle">{{ greeting }}. Here's what's happening across Screenkode.</p>
      </div>
      <div class="pg-actions">
        <router-link :to="{ name: TICKETS_ROUTE }" class="dn-toolbar-btn">
          <i class="pi pi-ticket"></i>
          <span>Tickets</span>
        </router-link>
        <router-link :to="{ name: NEW_ORGANIZATION_ROUTE }" class="btn btn-custom-primary dn-add-btn">
          <i class="pi pi-plus"></i>
          <span>New Organization</span>
        </router-link>
      </div>
    </header>

    <div class="pg-body dashboard-page">
      <!-- ------------------------------ period ------------------------------ -->
      <!-- One row, above everything it scopes: every count and chart below is
           for this period, so the numbers always agree. -->
      <div class="dash-filters">
        <div class="dash-ranges" role="group" aria-label="Period">
          <button
            v-for="option in DASHBOARD_RANGES"
            :key="option.value"
            type="button"
            class="dash-ranges__option"
            :class="{ 'is-active': range === option.value }"
            :aria-pressed="range === option.value ? 'true' : 'false'"
            @click="setRange(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <p class="dash-filters__note">
          <i class="pi pi-clock" aria-hidden="true"></i>
          <span>{{ periodNote }}</span>
        </p>
      </div>

      <div v-if="failed && !data" class="sp dash-failed" role="alert">
        <div class="dn-empty">
          <div class="dn-empty__icon dash-failed__icon"><i class="pi pi-exclamation-circle"></i></div>
          <h6>The dashboard couldn't be loaded</h6>
          <p>Check your connection and try again.</p>
          <button type="button" class="btn btn-custom-primary dn-add-btn" @click="load">
            <i class="pi pi-refresh"></i>
            <span>Try again</span>
          </button>
        </div>
      </div>

      <template v-else>
        <!-- ------------------------------- stats ------------------------------- -->
        <!-- The platform's four, and Kode AI's day (its own page has the rest). -->
        <stat-cards :stats="dashboardStats" :loading="initialLoading" :columns="5" label="Platform stats" />

        <!-- A reload keeps what's shown, dimmed, until the new numbers land. -->
        <div class="dash-sections" :class="{ 'is-refreshing': refreshing }" :aria-busy="isLoading ? 'true' : 'false'">
          <!-- ------------------------------ activity ------------------------------ -->
          <section class="sp dash-panel" aria-labelledby="dash-activity-title">
            <header class="sp__head">
              <span class="sp__icon" aria-hidden="true"><i class="pi pi-chart-line"></i></span>
              <div class="sp__head-text">
                <h2 id="dash-activity-title" class="sp__title">Activity</h2>
                <p class="sp__desc">Everything done across all organizations {{ period.within }}, by the app it was done in.</p>
              </div>
              <div class="sp__head-actions">
                <view-toggle v-model="views.activity" chart-icon="pi-chart-line" label="Show activity as" />
              </div>
            </header>
            <div class="sp__body dash-panel__body">
              <ul class="dash-figures" aria-label="Activity totals">
                <li v-for="figure in activityFigures" :key="figure.key" class="dash-figure">
                  <span class="dash-figure__label">{{ figure.label }}</span>
                  <span v-if="initialLoading" class="dash-skeleton dash-skeleton--figure" aria-hidden="true"></span>
                  <span v-else class="dash-figure__value" :title="figure.title || null">{{ figure.value }}</span>
                  <span v-if="initialLoading" class="dash-skeleton dash-skeleton--line" aria-hidden="true"></span>
                  <span v-else class="dash-figure__delta" :class="`dash-figure__delta--${figure.tone}`" :title="figure.delta">{{ figure.delta }}</span>
                </li>
              </ul>
              <div v-if="initialLoading" class="dash-skeleton dash-skeleton--chart" aria-hidden="true"></div>
              <line-chart
                v-else
                :points="activityPoints"
                :series="activitySeries"
                :view="views.activity"
                :period-label="bucketLabel"
                :loading="refreshing"
                label="Activity over time, by app"
                :empty-text="`No activity ${period.within}`"
              />
            </div>
          </section>

          <!-- ------------------------------ breakdowns ----------------------------- -->
          <div class="dash-grid dash-grid--two">
            <section class="sp dash-panel" aria-labelledby="dash-actions-title">
              <header class="sp__head">
                <span class="sp__icon" aria-hidden="true"><i class="pi pi-list"></i></span>
                <div class="sp__head-text">
                  <h2 id="dash-actions-title" class="sp__title">What was done</h2>
                  <p class="sp__desc">Activity {{ period.within }}, by the kind of action.</p>
                </div>
                <div class="sp__head-actions">
                  <view-toggle v-model="views.actions" label="Show actions as" />
                </div>
              </header>
              <div class="sp__body dash-panel__body">
                <div v-if="initialLoading" class="dash-skeleton dash-skeleton--bars" aria-hidden="true"></div>
                <bar-chart
                  v-else
                  :items="actionItems"
                  :view="views.actions"
                  :loading="refreshing"
                  label="Activity by action"
                  category-label="Action"
                  value-label="events"
                  :empty-text="`No activity ${period.within}`"
                />
              </div>
            </section>

            <section class="sp dash-panel" aria-labelledby="dash-top-title">
              <header class="sp__head">
                <span class="sp__icon" aria-hidden="true"><i class="pi pi-building"></i></span>
                <div class="sp__head-text">
                  <h2 id="dash-top-title" class="sp__title">Most active organizations</h2>
                  <p class="sp__desc">The most activity {{ period.within }}. Pick one to open its log.</p>
                </div>
                <div class="sp__head-actions">
                  <view-toggle v-model="views.organizations" label="Show organizations as" />
                </div>
              </header>
              <div class="sp__body dash-panel__body">
                <div v-if="initialLoading" class="dash-skeleton dash-skeleton--bars" aria-hidden="true"></div>
                <bar-chart
                  v-else
                  :items="topOrganizationItems"
                  :view="views.organizations"
                  :loading="refreshing"
                  selectable
                  label="Most active organizations"
                  category-label="Organization"
                  value-label="events"
                  :empty-text="`No organization was active ${period.within}`"
                  @select="viewOrganizationActivity"
                />
              </div>
            </section>
          </div>

          <div class="dash-grid dash-grid--three">
            <section class="sp dash-panel" aria-labelledby="dash-apps-title">
              <header class="sp__head">
                <span class="sp__icon" aria-hidden="true"><i class="pi pi-th-large"></i></span>
                <div class="sp__head-text">
                  <h2 id="dash-apps-title" class="sp__title">Organizations by app</h2>
                  <p class="sp__desc">Which apps their people can sign in to.</p>
                </div>
              </header>
              <div class="sp__body dash-panel__body">
                <div v-if="initialLoading" class="dash-skeleton dash-skeleton--bars" aria-hidden="true"></div>
                <div v-else class="dash-stack">
                  <div class="dash-stack__bar" role="img" :aria-label="appStackLabel">
                    <span
                      v-for="segment in appSegments.filter((s) => s.value > 0)"
                      :key="segment.key"
                      class="dash-stack__segment"
                      :style="{ flexGrow: segment.value, background: segment.color }"
                      :title="`${segment.label}: ${format(segment.value)} (${segment.percent}%)`"
                    ></span>
                    <span v-if="!organizationsTotal" class="dash-stack__segment dash-stack__segment--none"></span>
                  </div>
                  <ul class="dash-stack__legend">
                    <li v-for="segment in appSegments" :key="segment.key" class="dash-stack__item">
                      <span class="dash-stack__swatch" :style="{ background: segment.color }" aria-hidden="true"></span>
                      <span class="dash-stack__name">{{ segment.label }}</span>
                      <span class="dash-stack__value">
                        {{ format(segment.value) }}
                        <span class="dash-stack__pct">· {{ segment.percent }}%</span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section class="sp dash-panel" aria-labelledby="dash-roles-title">
              <header class="sp__head">
                <span class="sp__icon" aria-hidden="true"><i class="pi pi-users"></i></span>
                <div class="sp__head-text">
                  <h2 id="dash-roles-title" class="sp__title">Users by role</h2>
                  <p class="sp__desc">Everyone with an account, by their role.</p>
                </div>
                <div class="sp__head-actions">
                  <view-toggle v-model="views.roles" label="Show roles as" />
                </div>
              </header>
              <div class="sp__body dash-panel__body">
                <div v-if="initialLoading" class="dash-skeleton dash-skeleton--bars" aria-hidden="true"></div>
                <bar-chart
                  v-else
                  :items="roleItems"
                  :view="views.roles"
                  :loading="refreshing"
                  label="Users by role"
                  category-label="Role"
                  value-label="users"
                  empty-text="No users yet"
                />
              </div>
            </section>

            <section class="sp dash-panel" aria-labelledby="dash-health-title">
              <header class="sp__head">
                <span class="sp__icon" aria-hidden="true"><i class="pi pi-heart"></i></span>
                <div class="sp__head-text">
                  <h2 id="dash-health-title" class="sp__title">Platform health</h2>
                  <p class="sp__desc">How much of the platform is switched on.</p>
                </div>
              </header>
              <div class="sp__body dash-panel__body">
                <div v-if="initialLoading" class="dash-skeleton dash-skeleton--bars" aria-hidden="true"></div>
                <ul v-else class="dash-meters" aria-label="Platform health">
                  <li v-for="meter in meters" :key="meter.key" class="dash-meter">
                    <div class="dash-meter__head">
                      <span class="dash-meter__label">
                        <i class="pi" :class="meter.icon" aria-hidden="true"></i>
                        <span>{{ meter.label }}</span>
                      </span>
                      <span class="dash-meter__value">{{ meter.text }}</span>
                    </div>
                    <div
                      class="dash-meter__track"
                      role="meter"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      :aria-valuenow="meter.percent"
                      :aria-valuetext="meter.text"
                      :aria-label="meter.label"
                    >
                      <span class="dash-meter__fill" :style="{ width: `${meter.percent}%` }"></span>
                    </div>
                    <span class="dash-meter__hint">{{ meter.hint }}</span>
                  </li>
                </ul>
              </div>
            </section>
          </div>

          <!-- --------------------------------- lists --------------------------------- -->
          <div class="dash-grid dash-grid--lists">
            <section class="sp dash-panel" aria-labelledby="dash-recent-title">
              <header class="sp__head">
                <span class="sp__icon" aria-hidden="true"><i class="pi pi-history"></i></span>
                <div class="sp__head-text">
                  <h2 id="dash-recent-title" class="sp__title">Recent activity</h2>
                  <p class="sp__desc">The latest things done, across every organization.</p>
                </div>
              </header>
              <div class="sp__body dash-panel__body">
                <div v-if="initialLoading" class="dash-list" aria-busy="true">
                  <div v-for="n in 6" :key="n" class="dash-row dash-row--skeleton" aria-hidden="true">
                    <span class="dash-skeleton dash-skeleton--avatar dash-skeleton--round"></span>
                    <span class="dash-row__meta">
                      <span class="dash-skeleton dash-skeleton--title"></span>
                      <span class="dash-skeleton dash-skeleton--sub"></span>
                    </span>
                  </div>
                </div>
                <div v-else-if="!recentEntries.length" class="dn-empty">
                  <div class="dn-empty__icon"><i class="pi pi-history"></i></div>
                  <h6>No activity yet</h6>
                  <p>What people do in the apps shows up here as it happens.</p>
                </div>
                <recent-activity v-else :entries="recentEntries" :loading="refreshing" @organization="viewOrganizationActivity" />
              </div>
            </section>

            <div class="dash-col">
              <!-- ----------------------- recent organizations ----------------------- -->
              <section class="sp dash-panel" aria-labelledby="dash-orgs-title">
                <header class="sp__head">
                  <span class="sp__icon" aria-hidden="true"><i class="pi pi-building"></i></span>
                  <div class="sp__head-text">
                    <h2 id="dash-orgs-title" class="sp__title">Recent organizations</h2>
                    <p class="sp__desc">The newest congregations on Screenkode.</p>
                  </div>
                  <div class="sp__head-actions">
                    <router-link :to="{ name: ORGANIZATIONS_ROUTE }" class="dash-jump">
                      <span>View all</span>
                      <i class="pi pi-arrow-right dash-jump__arrow" aria-hidden="true"></i>
                    </router-link>
                  </div>
                </header>

                <div class="sp__body dash-panel__body">
                  <div v-if="initialLoading" class="dash-list" aria-busy="true">
                    <div v-for="n in 4" :key="n" class="dash-row dash-row--skeleton" aria-hidden="true">
                      <span class="dash-skeleton dash-skeleton--avatar"></span>
                      <span class="dash-row__meta">
                        <span class="dash-skeleton dash-skeleton--title"></span>
                        <span class="dash-skeleton dash-skeleton--sub"></span>
                      </span>
                    </div>
                  </div>

                  <div v-else-if="!recentOrganizations.length" class="dn-empty">
                    <div class="dn-empty__icon"><i class="pi pi-building"></i></div>
                    <h6>No organizations yet</h6>
                    <p>Organizations you add show up here, newest first.</p>
                    <router-link :to="{ name: NEW_ORGANIZATION_ROUTE }" class="btn btn-custom-primary dn-add-btn">
                      <i class="pi pi-plus"></i>
                      <span>New Organization</span>
                    </router-link>
                  </div>

                  <ul v-else class="dash-list">
                    <li v-for="organization in recentOrganizations" :key="organization.id">
                      <button type="button" class="dash-row" @click="viewOrganization(organization)">
                        <span
                          class="dn-avatar dash-row__avatar"
                          :class="logoUrl(organization) ? 'dn-avatar--img row-avatar' : ['row-avatar', avatarColorClass(organization.name)]"
                        >
                          <img v-if="logoUrl(organization)" :src="logoUrl(organization)" alt="" />
                          <template v-else>{{ initialsOf(organization.name) }}</template>
                        </span>
                        <span class="dash-row__meta">
                          <span class="dash-row__name">{{ organization.name }}</span>
                          <span class="dash-row__sub">{{ organizationLocation(organization) }}</span>
                        </span>
                        <span class="dash-row__aside">
                          <span
                            class="status-pill"
                            :class="organization.isActive ? 'status-pill--active' : 'status-pill--offline'"
                          >
                            {{ organization.isActive ? 'Active' : 'Disabled' }}
                          </span>
                          <span class="dash-row__time" :title="$filters.getDateOnly(organization.createdAt)">{{ timeAgo(organization.createdAt) }}</span>
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
              </section>

              <!-- ---------------------------- open tickets --------------------------- -->
              <section class="sp dash-panel" aria-labelledby="dash-tickets-title">
                <header class="sp__head">
                  <span class="sp__icon" aria-hidden="true"><i class="pi pi-ticket"></i></span>
                  <div class="sp__head-text">
                    <h2 id="dash-tickets-title" class="sp__title">Open tickets</h2>
                    <p class="sp__desc">Support requests waiting on the team.</p>
                  </div>
                  <div class="sp__head-actions">
                    <router-link :to="{ name: TICKETS_ROUTE }" class="dash-jump">
                      <span>View all</span>
                      <i class="pi pi-arrow-right dash-jump__arrow" aria-hidden="true"></i>
                    </router-link>
                  </div>
                </header>

                <div class="sp__body dash-panel__body">
                  <div v-if="initialLoading" class="dash-list" aria-busy="true">
                    <div v-for="n in 3" :key="n" class="dash-row dash-row--skeleton" aria-hidden="true">
                      <span class="dash-row__meta">
                        <span class="dash-skeleton dash-skeleton--title"></span>
                        <span class="dash-skeleton dash-skeleton--sub"></span>
                      </span>
                    </div>
                  </div>

                  <div v-else-if="!openTickets.length" class="dn-empty">
                    <div class="dn-empty__icon"><i class="pi pi-check-circle"></i></div>
                    <h6>All caught up</h6>
                    <p>There are no open support tickets right now.</p>
                  </div>

                  <ul v-else class="dash-list">
                    <li v-for="ticket in openTickets" :key="ticket.id">
                      <button type="button" class="dash-row" @click="viewTicket(ticket)">
                        <span class="dash-ticket-no">#{{ ticket.number }}</span>
                        <span class="dash-row__meta">
                          <span class="dash-row__name">{{ ticket.subject }}</span>
                          <span class="dash-row__sub">{{ ticketSource(ticket) }}</span>
                        </span>
                        <span class="dash-row__aside">
                          <span class="dash-row__time">{{ timeAgo(ticket.lastModifiedAt || ticket.createdAt) }}</span>
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
        </div>
      </template>
    </div>
  </app-shell>
</template>
<script>
import appShell from '@/components/App_Shell.vue';
import statCards from '@/components/Stat_Cards.vue';
import lineChart from '@/components/charts/Line_Chart.vue';
import barChart from '@/components/charts/Bar_Chart.vue';
import viewToggle from '@/components/charts/View_Toggle.vue';
import recentActivity from '@/components/dashboard/Recent_Activity.vue';
import viewTicketModal from '@/components/ticket/View_Ticket_Modal.vue';
import {
  NEW_ORGANIZATION_ROUTE,
  ORGANIZATIONS_ROUTE,
  ORGANIZATION_ACTIVITY_ROUTE,
  ORGANIZATION_DETAILS_ROUTE,
  TICKETS_ROUTE,
  KODE_AI_ROUTE,
} from '@/router/routes';
import { GET_DASHBOARD } from '@/store/action_types';
import { watchKodeAiUsage } from '@/services/kode_ai';
import { formatCount, KODE_AI_POLL_MS } from '@/utils/kode_ai_usage';
import { activityAction } from '@/utils/activity';
import {
  ACTIVITY_CHANNELS,
  CHART_COLORS,
  DASHBOARD_RANGES,
  DEFAULT_DASHBOARD_RANGE,
  bucketLabels,
  compactNumber,
  compare,
  dashboardRange,
  percentOf,
  timeZoneShort,
  viewerTimeZone,
} from '@/utils/dashboard';
import { avatarColorClass, formatRole, initialsOf, organizationLogoUrl, timeAgo } from '@/utils/display';

// Kinds of action shown on their own before the rest fold into "Other".
const ACTIONS_SHOWN = 8;
// The period comes back on the next visit (this browser only).
const RANGE_STORAGE_KEY = 'dashboard.range';

const rememberedRange = () => {
  try {
    const value = window.localStorage.getItem(RANGE_STORAGE_KEY);
    return DASHBOARD_RANGES.some((range) => range.value === value) ? value : DEFAULT_DASHBOARD_RANGE;
  } catch (error) {
    return DEFAULT_DASHBOARD_RANGE;
  }
};

export default {
  title: 'Dashboard',
  components: {
    appShell,
    statCards,
    lineChart,
    barChart,
    viewToggle,
    recentActivity,
    viewTicketModal,
  },
  data() {
    return {
      NEW_ORGANIZATION_ROUTE,
      ORGANIZATIONS_ROUTE,
      TICKETS_ROUTE,
      DASHBOARD_RANGES,
      range: rememberedRange(),
      timeZone: viewerTimeZone(),
      // Chart or table, per panel.
      views: { activity: 'chart', actions: 'chart', organizations: 'chart', roles: 'chart' },
      showViewTicketModal: false,
      selectedTicket: null,
      // Kode AI's day (services/kode_ai), for its card; kept current on its own.
      kodeAi: null,
      kodeAiFailed: false,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    greeting() {
      const hour = new Date().getHours();
      const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
      const name = (this.account?.fname || '').trim();
      return name ? `${part}, ${this.$filters.capitalize(name)}` : part;
    },
    dashboard() {
      return this.$store.getters.dashboard;
    },
    data() {
      return this.dashboard.data;
    },
    isLoading() {
      return this.dashboard.isLoading;
    },
    failed() {
      return this.dashboard.failed;
    },
    // Nothing to show yet: skeletons. Reloading: what's shown stays, dimmed.
    initialLoading() {
      return !this.data && (this.isLoading || !this.failed);
    },
    refreshing() {
      return this.isLoading && Boolean(this.data);
    },
    period() {
      return dashboardRange(this.range);
    },
    zone() {
      return this.data?.range?.timezone || this.timeZone;
    },
    bucketKind() {
      return this.data?.range?.bucket === 'hour' ? 'hour' : 'day';
    },
    bucketLabel() {
      return this.bucketKind === 'hour' ? 'Hour' : 'Day';
    },
    periodNote() {
      const zone = `Times in your time zone (${timeZoneShort(this.zone)})`;
      const loadedAt = this.dashboard.loadedAt;
      return loadedAt ? `${zone} · Updated ${this.$filters.getTimeOnly(loadedAt)}` : zone;
    },

    // ------------------------------- stats -------------------------------
    stats() {
      const d = this.data;
      const organizations = d?.organizations || {};
      const users = d?.users || {};
      const screens = d?.screens || {};
      const tickets = d?.tickets || {};
      const within = this.period.within;
      const previous = this.period.previous;
      // "+3 joined in the last 7 days", or "No organizations joined in the last 7 days".
      const joinedDelta = (count, verb, plural) => (count
        ? { text: `+${this.format(count)} ${verb} ${within}`, tone: 'up' }
        : { text: `No ${plural} ${verb} ${within}`, tone: 'muted' });

      const organizationsJoined = joinedDelta(organizations.joined, 'joined', 'organizations');
      const usersJoined = joinedDelta(users.joined, 'joined', 'users');
      const screensAdded = joinedDelta(screens.added, 'added', 'screens');
      const opened = Number(tickets.opened) || 0;
      const resolved = Number(tickets.resolved) || 0;
      const openNow = Number(tickets.open) || 0;

      return [
        {
          key: 'organizations',
          label: 'Organizations',
          value: compactNumber(organizations.total),
          delta: organizationsJoined.text,
          deltaTone: organizationsJoined.tone,
          note: compare(organizations.joined, organizations.joinedPrevious, previous).text,
          trend: organizations.series,
          icon: 'pi-building',
          tone: 'primary',
        },
        {
          key: 'users',
          label: 'Users',
          value: compactNumber(users.total),
          delta: usersJoined.text,
          deltaTone: usersJoined.tone,
          note: compare(users.joined, users.joinedPrevious, previous).text,
          trend: users.series,
          icon: 'pi-users',
          tone: 'success',
        },
        {
          key: 'screens',
          label: 'Screens',
          value: compactNumber(screens.total),
          delta: screensAdded.text,
          deltaTone: screensAdded.tone,
          note: compare(screens.added, screens.addedPrevious, previous).text,
          trend: screens.series,
          icon: 'pi-desktop',
          tone: 'warning',
        },
        {
          key: 'tickets',
          label: 'Open Tickets',
          value: compactNumber(openNow),
          delta: opened || resolved
            ? `${this.format(opened)} opened · ${this.format(resolved)} resolved ${within}`
            : openNow ? 'Waiting on a reply' : 'All caught up',
          deltaTone: openNow ? 'info' : 'up',
          note: compare(opened, tickets.openedPrevious, previous, true).text,
          trend: tickets.series,
          icon: openNow ? 'pi-ticket' : 'pi-check-circle',
          tone: openNow ? 'danger' : 'success',
        },
      ];
    },

    // Kode AI's card: today's requests, tokens and questions. Today is
    // Google's day, midnight to midnight Pacific, which its limits reset on.
    kodeAiStat() {
      const totals = this.kodeAi ? this.kodeAi.totals : null;
      const value = (key) => (totals ? formatCount(totals[key]) : '—');
      return {
        key: 'kodeAi',
        label: 'Kode AI usage',
        icon: 'pi-sparkles',
        tone: 'primary',
        to: { name: KODE_AI_ROUTE },
        title: 'Open Kode AI. Today runs midnight to midnight Pacific time, as Google\'s daily limits do.',
        loading: !this.kodeAi && !this.kodeAiFailed,
        rows: [
          { key: 'requests', label: 'Requests today', value: value('requests') },
          { key: 'tokens', label: 'Tokens today', value: value('totalTokens') },
          { key: 'questions', label: 'Questions today', value: value('questions') },
        ],
      };
    },
    dashboardStats() {
      return [...this.stats, this.kodeAiStat];
    },

    // ------------------------------ activity ------------------------------
    activity() {
      return this.data?.activity || {};
    },
    activityFigures() {
      const a = this.activity;
      const previous = this.period.previous;
      const figure = (key, label, current, before, upIsBad = false) => {
        const delta = compare(current || 0, before || 0, previous, upIsBad);
        return { key, label, value: compactNumber(current), delta: delta.text, tone: delta.tone };
      };
      const peak = a.peak
        ? bucketLabels(a.peak.at, this.bucketKind, this.zone)
        : null;
      return [
        figure('events', 'Events', a.total, a.previousTotal),
        figure('signIns', 'Sign-ins', a.signIns, a.signInsPrevious),
        figure('failed', 'Failed sign-ins', a.failedSignIns, a.failedSignInsPrevious, true),
        figure('users', 'Active users', a.activeUsers, a.activeUsersPrevious),
        figure('organizations', 'Active organizations', a.activeOrganizations, a.activeOrganizationsPrevious),
        {
          key: 'peak',
          label: `Busiest ${this.bucketLabel.toLowerCase()}`,
          value: peak ? peak.short : '—',
          title: peak ? peak.full : null,
          delta: a.peak ? `${this.format(a.peak.count)} ${a.peak.count === 1 ? 'event' : 'events'}` : `Nothing ${this.period.within}`,
          tone: 'muted',
        },
      ];
    },
    activityPoints() {
      const buckets = this.data?.buckets || [];
      return buckets.map((bucket) => ({ key: bucket.key, at: bucket.at, ...bucketLabels(bucket.at, this.bucketKind, this.zone) }));
    },
    // One series per app.
    activitySeries() {
      const series = this.activity.series || {};
      return ACTIVITY_CHANNELS.map((channel) => ({
        key: channel.key,
        label: channel.label,
        color: channel.color,
        values: series[channel.key] || [],
      }));
    },
    actionItems() {
      const rows = (this.activity.byAction || []).filter((row) => row.count > 0);
      const shown = rows.slice(0, ACTIONS_SHOWN).map((row) => ({
        key: row.action,
        label: activityAction(row.action).label,
        value: row.count,
      }));
      const rest = rows.slice(ACTIONS_SHOWN);
      if (rest.length) {
        shown.push({
          key: 'other',
          label: `Other (${rest.length} ${rest.length === 1 ? 'kind' : 'kinds'})`,
          value: rest.reduce((sum, row) => sum + row.count, 0),
        });
      }
      return shown;
    },
    topOrganizationItems() {
      return (this.activity.topOrganizations || []).map((organization) => ({
        key: organization.id,
        label: organization.name,
        sublabel: organization.isActive ? '' : 'Disabled',
        value: organization.count,
      }));
    },

    // ----------------------------- breakdowns -----------------------------
    organizationsTotal() {
      return Number(this.data?.organizations?.total) || 0;
    },
    appSegments() {
      const byApp = this.data?.organizations?.byApp || {};
      const total = this.organizationsTotal;
      return [
        { key: 'screenkode', label: 'Screenkode only', value: Number(byApp.screenkode) || 0, color: CHART_COLORS.screenkode },
        { key: 'both', label: 'Both apps', value: Number(byApp.both) || 0, color: CHART_COLORS.admin },
        { key: 'chabad_digital', label: 'Chabad Digital only', value: Number(byApp.chabad_digital) || 0, color: CHART_COLORS.chabad_digital },
      ].map((segment) => ({ ...segment, percent: percentOf(segment.value, total) }));
    },
    appStackLabel() {
      return `Organizations by app: ${this.appSegments.map((segment) => `${segment.label} ${this.format(segment.value)}`).join(', ')}`;
    },
    roleItems() {
      return (this.data?.users?.byRole || []).map((row) => ({
        key: row.role,
        label: formatRole(row.role),
        value: row.count,
      }));
    },
    meters() {
      const organizations = this.data?.organizations || {};
      const users = this.data?.users || {};
      const screens = this.data?.screens || {};
      const tickets = this.data?.tickets || {};
      const allTickets = (Number(tickets.open) || 0) + (Number(tickets.closed) || 0);
      const plural = (count, one, many) => `${this.format(count)} ${count === 1 ? one : many}`;
      const meter = (key, icon, label, part, whole, hint) => ({
        key,
        icon,
        label,
        percent: percentOf(part, whole),
        text: `${this.format(part)} of ${this.format(whole)}`,
        hint,
      });
      return [
        meter('organizations', 'pi-building', 'Active organizations', organizations.active || 0, organizations.total || 0,
          `${plural(organizations.disabled || 0, 'is disabled', 'are disabled')} · ${plural(organizations.archived || 0, 'archived', 'archived')}`),
        meter('users', 'pi-users', 'Active users', users.active || 0, users.total || 0,
          `${plural(users.disabled || 0, 'is disabled', 'are disabled')} · ${plural(users.pendingInvites || 0, 'invitation pending', 'invitations pending')}`),
        meter('screens', 'pi-desktop', 'Active screens', screens.active || 0, screens.total || 0,
          plural(screens.disabled || 0, 'is disabled', 'are disabled')),
        meter('tickets', 'pi-ticket', 'Tickets resolved', tickets.closed || 0, allTickets,
          plural(tickets.open || 0, 'is still open', 'are still open')),
      ];
    },

    // -------------------------------- lists --------------------------------
    recentEntries() {
      return this.activity.recent || [];
    },
    recentOrganizations() {
      return this.data?.recentOrganizations || [];
    },
    openTickets() {
      return this.data?.openTickets || [];
    },
  },
  methods: {
    avatarColorClass,
    initialsOf,
    timeAgo,
    format(value) {
      return (Number(value) || 0).toLocaleString('en-US');
    },
    logoUrl(organization) {
      return organizationLogoUrl(organization?.logo);
    },
    organizationLocation(organization) {
      const parts = [organization.address, organization.postCode].filter(Boolean);
      return parts.length ? parts.join(', ') : organization.email || 'No address yet';
    },
    ticketSource(ticket) {
      const organization = ticket.organization?.name;
      const user = [ticket.user?.fname, ticket.user?.lname]
        .filter(Boolean)
        .map((part) => this.$filters.capitalize(part))
        .join(' ');
      return [organization, user].filter(Boolean).join(' · ') || 'Unknown sender';
    },
    viewOrganization(organization) {
      this.$router.push({
        name: ORGANIZATION_DETAILS_ROUTE,
        params: { id: organization.id, name: organization.name },
      });
    },
    // From a ranking row or a feed entry: its organization's activity log.
    viewOrganizationActivity(organization) {
      const id = organization.id || organization.key;
      const name = organization.name || organization.label;
      if (!id) return;
      this.$router.push({ name: ORGANIZATION_ACTIVITY_ROUTE, params: { id, name } });
    },
    viewTicket(ticket) {
      this.selectedTicket = ticket;
      this.showViewTicketModal = true;
    },
    setRange(value) {
      if (value === this.range) return;
      this.range = value;
      try {
        window.localStorage.setItem(RANGE_STORAGE_KEY, value);
      } catch (error) {
        // A private window: the period just isn't remembered.
      }
      this.load();
    },
    load() {
      return this.$store.dispatch(GET_DASHBOARD, { range: this.range, tz: this.timeZone });
    },
  },
  mounted() {
    this.load();
    this.kodeAiWatch = watchKodeAiUsage(KODE_AI_POLL_MS, (result) => {
      if (result.ok && result.data) {
        this.kodeAi = result.data;
        this.kodeAiFailed = false;
      } else if (!this.kodeAi) {
        this.kodeAiFailed = true;
      }
    });
  },
  beforeUnmount() {
    if (this.kodeAiWatch) this.kodeAiWatch.stop();
  },
};
</script>
<style scoped>
/* ------------------------------ filter row ------------------------------ */
.dash-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
}

.dash-ranges {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  box-shadow: var(--shadow-xs);
}

.dash-ranges__option {
  min-height: 30px;
  padding: 5px 14px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--fs-sm);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease, box-shadow .15s ease;
}

.dash-ranges__option:hover {
  background: var(--color-bg-soft);
  color: var(--color-text-primary);
}

.dash-ranges__option.is-active {
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.dash-ranges__option:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus) !important;
}

.dash-filters__note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
}

.dash-filters__note .pi {
  font-size: 0.72rem;
}

/* -------------------------------- panels -------------------------------- */
.dash-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: opacity .15s ease;
}

.dash-sections.is-refreshing {
  opacity: 0.85;
}

.dash-grid {
  display: grid;
  gap: 20px;
  align-items: start;
}

.dash-grid--two {
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
}

.dash-grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.dash-grid--lists {
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
}

.dash-col {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

/* `.sp + .sp` spaces stacked panels; here the gaps do that. */
.dash-sections .sp + .sp,
.dash-col .sp + .sp {
  margin-top: 0;
}

.dash-panel {
  min-width: 0;
}

.dash-panel__body {
  padding: 8px 12px 12px;
}

.dash-panel .sp__head-actions {
  align-self: center;
}

.dash-failed {
  padding: 8px;
}

.dash-failed__icon {
  background: #FEECEC !important;
  color: #C81E1E !important;
}

.dash-jump {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 32px;
  padding: 5px 12px;
  color: var(--color-primary-hover);
  font-size: var(--fs-sm);
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none !important;
  background: var(--color-bg-canvas);
  border: 1px solid var(--color-primary-border);
  border-radius: var(--radius-md);
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.dash-jump:hover {
  color: var(--color-primary-pressed);
  background: var(--color-primary-soft);
  border-color: var(--color-primary);
}

.dash-jump__arrow {
  font-size: 0.62rem;
  transition: transform .15s ease;
}

.dash-jump:hover .dash-jump__arrow {
  transform: translateX(2px);
}

/* ---------------------------- activity figures --------------------------- */
.dash-figures {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin: 4px 0 16px;
  padding: 0;
  list-style: none;
}

.dash-figure {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--color-divider);
  border-radius: var(--radius-md);
  background: var(--color-bg-soft);
  list-style: none;
}

.dash-figure__label {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-figure__value {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-figure__delta {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-figure__delta--up { color: #0E9F6E; }
.dash-figure__delta--down { color: #C81E1E; }

/* ---------------------------- stacked bar ---------------------------- */
.dash-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 6px 4px 4px;
}

/* A 2px gap in the surface colour separates the segments. */
.dash-stack__bar {
  display: flex;
  gap: 2px;
  height: 14px;
  overflow: hidden;
  border-radius: var(--radius-pill);
}

.dash-stack__segment {
  display: block;
  flex: 0 1 0%;
  min-width: 4px;
  transition: flex-grow .25s ease;
}

.dash-stack__segment--none {
  flex-grow: 1;
  background: var(--color-bg-muted);
}

.dash-stack__legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.dash-stack__item {
  display: flex;
  align-items: center;
  gap: 8px;
  list-style: none;
}

.dash-stack__swatch {
  flex: none;
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.dash-stack__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--fs-sm);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-stack__value {
  flex: none;
  color: var(--color-text-secondary);
  font-size: var(--fs-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.dash-stack__pct {
  color: var(--color-text-muted);
  font-weight: 500;
}

/* -------------------------------- meters -------------------------------- */
.dash-meters {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 6px 4px 2px;
  list-style: none;
}

.dash-meter {
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style: none;
}

.dash-meter__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.dash-meter__label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: var(--color-text-primary);
  font-size: var(--fs-sm);
  font-weight: 500;
}

.dash-meter__label span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-meter__label .pi {
  color: var(--color-text-muted);
  font-size: 0.78rem;
}

.dash-meter__value {
  flex: none;
  color: var(--color-text-secondary);
  font-size: var(--fs-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* The track is a lighter step of the fill's own colour, so the whole bar
   reads as one scale. */
.dash-meter__track {
  height: 8px;
  overflow: hidden;
  border-radius: var(--radius-pill);
  background: var(--color-primary-soft-2);
}

.dash-meter__fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  background: var(--color-primary);
  transition: width .25s ease;
}

.dash-meter__hint {
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
}

/* --------------------------------- rows --------------------------------- */
.dash-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.dash-list li {
  list-style: none;
}

.dash-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.dash-row:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border) !important;
}

.dash-row:focus-visible {
  box-shadow: var(--shadow-focus) !important;
}

.dash-row--skeleton {
  cursor: default;
}

.dash-row--skeleton:hover {
  background: transparent;
  border-color: transparent !important;
}

.dash-row__avatar {
  width: 38px;
  height: 38px;
}

.dash-row__meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 auto;
  min-width: 0;
}

.dash-row__name {
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-row__sub {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-row__aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  flex-shrink: 0;
}

.dash-row__time {
  color: var(--color-text-muted);
  font-size: 0.72rem;
  white-space: nowrap;
}

.dash-ticket-no {
  flex-shrink: 0;
  min-width: 44px;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
  font-size: 0.72rem;
  font-weight: 700;
  text-align: center;
}

/* ------------------------------- skeleton ------------------------------- */
.dash-skeleton {
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 45%, #eef2f7 100%);
  background-size: 220% 100%;
  animation: dash-shimmer 1.15s ease-in-out infinite;
}

.dash-skeleton--figure {
  width: 48px;
  height: 20px;
  margin: 2px 0;
  border-radius: var(--radius-sm);
}

.dash-skeleton--line {
  width: 70%;
  height: 9px;
}

.dash-skeleton--chart {
  width: 100%;
  height: 280px;
  border-radius: var(--radius-md);
}

.dash-skeleton--bars {
  width: 100%;
  height: 200px;
  border-radius: var(--radius-md);
}

.dash-skeleton--avatar {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
}

.dash-skeleton--round {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.dash-skeleton--title {
  width: 58%;
  height: 11px;
}

.dash-skeleton--sub {
  width: 36%;
  height: 9px;
}

@keyframes dash-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .dash-skeleton {
    animation: none;
  }
}

/* ------------------------------ responsive ------------------------------ */
@media (max-width: 1400px) {
  .dash-figures {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1280px) {
  .dash-grid--two,
  .dash-grid--lists {
    grid-template-columns: minmax(0, 1fr);
  }

  .dash-grid--three {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .dash-grid--three {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 575.98px) {
  .dash-filters {
    align-items: stretch;
  }

  .dash-ranges {
    display: flex;
  }

  .dash-ranges__option {
    flex: 1 1 0;
    padding-left: 8px;
    padding-right: 8px;
  }

  .dash-figures {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sp__head {
    flex-wrap: wrap;
  }

  .dash-row__time {
    display: none;
  }
}
</style>
